"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Edit,
  Check,
  Trash,
  ArrowLeft,
  User as UserIcon,
  FileText,
  Phone,
} from "lucide-react";
import ImageComponent from "../../../../components/form-components/ImageComponent";
import { useUser } from "../../../../hooks/useUser";
import { User } from "../../../../types/User";
import LoadingComponent from "../../../../components/form-components/LoadingComponent";
import Spinner from "../../../Spinner";
import CustomDropdown from "../../../CustomDropdown";

const USER_ROLES = [
  { value: "driver", label: "Driver" },
  { value: "merchant", label: "Merchant" },
  { value: "merchant-driver", label: "Merchant Driver" },
  { value: "customer", label: "Customer" },
];

const VERIFIED_OPTIONS = [
  { value: true, label: "Verified" },
  { value: false, label: "Not Verified" },
];

type UpdatedFields = Partial<Omit<User, "aadharCard" | "panCard">> & {
  aadharCard?: File | string;
  panCard?: File | string;
  [key: string]: any;
};

const useClickOutside = (
  ref: React.RefObject<HTMLDivElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const UserInfo = () => {
  const { userId } = useParams<{ userId: string }>();
  const { getUser, updateProfile, deleteAccount } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedFields, setUpdatedFields] = useState<UpdatedFields>({});
  const [imageErrors, setImageErrors] = useState({
    aadharCard: "",
    panCard: "",
  });
  const navigate = useNavigate();

  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [isVerifiedDropdownOpen, setIsVerifiedDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const verifiedDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(roleDropdownRef, () => setIsRoleDropdownOpen(false));
  useClickOutside(verifiedDropdownRef, () => setIsVerifiedDropdownOpen(false));

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const user = await getUser(userId);
        setUserData(user);
      } catch (err) {
        setError("Failed to fetch user data");
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userId, getUser]);

  const handleFieldChange = (field: keyof User, value: any) => {
    setUserData((prev) => (prev ? { ...prev, [field]: value } : null));
    setUpdatedFields((prev) => ({ ...prev, [field]: value }));
  };

  const validateFile = useCallback((file: File, fieldName: string) => {
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/svg+xml",
      "application/pdf",
    ];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    if (!validTypes.includes(file.type)) {
      setImageErrors((prev) => ({
        ...prev,
        [fieldName]: "Invalid file type.",
      }));
      return false;
    }
    if (file.size > maxSize) {
      setImageErrors((prev) => ({
        ...prev,
        [fieldName]: "File size exceeds 5MB.",
      }));
      return false;
    }
    setImageErrors((prev) => ({ ...prev, [fieldName]: "" }));
    return true;
  }, []);

  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        const fieldName = e.target.name as "aadharCard" | "panCard";
        if (validateFile(file, fieldName)) {
          setUpdatedFields((prev) => ({ ...prev, [fieldName]: file }));
        }
      }
    },
    [validateFile],
  );

  const handleUpdate = async () => {
    if (!userId || !userData) return;
    setUpdating(true);
    const formData = new FormData();
    Object.entries(updatedFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    try {
      const updatedUser = await updateProfile(userId, formData);
      setUserData(updatedUser);
      setIsEditing(false);
      setUpdatedFields({});
    } catch (err) {
      setError("Failed to update user information");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    if (
      window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    ) {
      setUpdating(true);
      try {
        await deleteAccount(userId);
        navigate("/users");
      } catch (err) {
        setError("Failed to delete user.");
      } finally {
        setUpdating(false);
      }
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!userData) return <div className="p-6">No user data found</div>;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {userData.firstName} {userData.lastName}
              </h1>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${userData.isVerified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
              >
                {userData.isVerified ? "Verified" : "Not Verified"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDelete}
              className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-colors"
            >
              <Trash size={20} />
            </button>
            {isEditing ? (
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {updating ? <Spinner /> : <Check size={20} />}
                Save
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-300 transition-colors"
              >
                <Edit size={20} />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <InfoCard title="Personal Information" icon={<UserIcon />}>
              <DetailItem
                label="First Name"
                value={userData.firstName}
                name="firstName"
                onChange={(e) => handleFieldChange("firstName", e.target.value)}
                isEditing={isEditing}
              />
              <DetailItem
                label="Last Name"
                value={userData.lastName || ""}
                name="lastName"
                onChange={(e) => handleFieldChange("lastName", e.target.value)}
                isEditing={isEditing}
              />
              <DetailItem
                label="Address"
                value={userData.address || ""}
                name="address"
                onChange={(e) => handleFieldChange("address", e.target.value)}
                isEditing={isEditing}
              />
              <DetailItem
                label="City"
                value={userData.city || ""}
                name="city"
                onChange={(e) => handleFieldChange("city", e.target.value)}
                isEditing={isEditing}
              />
            </InfoCard>
            <InfoCard title="Contact Details" icon={<Phone />}>
              <DetailItem
                label="Email"
                value={userData.email}
                name="email"
                type="email"
                onChange={(e) => handleFieldChange("email", e.target.value)}
                isEditing={isEditing}
              />
              <DetailItem
                label="Phone"
                value={userData.phone}
                name="phone"
                type="tel"
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                isEditing={isEditing}
              />
            </InfoCard>
          </div>
          <div className="lg:col-span-1 space-y-6">
            <InfoCard title="Role & Status" icon={<UserIcon />}>
              <CustomDropdown
                label="Role"
                value={userData.type}
                options={USER_ROLES}
                isOpen={isRoleDropdownOpen}
                setIsOpen={setIsRoleDropdownOpen}
                onSelect={(value) => handleFieldChange("type", value)}
                isEditing={isEditing}
                dropdownRef={roleDropdownRef}
              />
              <CustomDropdown
                label="Verified"
                value={userData.isVerified}
                options={VERIFIED_OPTIONS}
                isOpen={isVerifiedDropdownOpen}
                setIsOpen={setIsVerifiedDropdownOpen}
                onSelect={(value) => handleFieldChange("isVerified", value)}
                isEditing={isEditing}
                dropdownRef={verifiedDropdownRef}
              />
            </InfoCard>
            <InfoCard title="Documents" icon={<FileText />}>
              <ImageComponent
                title="Aadhar Card"
                imageUrl={
                  userData.aadharCard
                    ? `https://movingrolls.online${userData.aadharCard}`
                    : ""
                }
                onFileChange={isEditing ? handleImageChange : undefined}
                error={imageErrors.aadharCard}
                name="aadharCard"
              />
              <ImageComponent
                title="PAN Card"
                imageUrl={
                  userData.panCard
                    ? `https://movingrolls.online${userData.panCard}`
                    : ""
                }
                onFileChange={isEditing ? handleImageChange : undefined}
                error={imageErrors.panCard}
                name="panCard"
              />
            </InfoCard>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center gap-3 mb-4">
      <div className="text-[#3c0b0b]">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  label: string;
  value: string;
  name: string;
  isEditing: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
}> = ({ label, value, name, isEditing, onChange, type = "text" }) => (
  <div>
    <label className="text-sm text-gray-500">{label}</label>
    {isEditing ? (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full p-2 border rounded-md mt-1"
      />
    ) : (
      <p className="font-medium text-gray-800">{value || "-"}</p>
    )}
  </div>
);

export default UserInfo;
